import { useEffect, useState } from "react";
import type { ApiClient, JourneyTrack, PatientDetail, PatientSummary } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";
import { subscribePatientStale } from "./patientBus";

export type PatientsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; patients: PatientSummary[]; nextCursor: string | null };

export function usePatients(track?: JourneyTrack): PatientsState {
  const api = useApi();
  const [state, setState] = useState<PatientsState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    api.patients
      .list(track ? { track } : undefined)
      .then((res) => {
        if (!cancelled) {
          setState({ status: "success", patients: res.items, nextCursor: res.nextCursor });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load patients" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, track]);

  return state;
}

export type PatientState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; patient: PatientDetail };

// Keyed by ApiClient instance (one per app session, see AuthProvider) rather
// than a bare module-level Map, so the cache lives for the session without a
// new provider and doesn't leak between tests, which each build a fresh
// fake ApiClient.
const patientCacheByApi = new WeakMap<ApiClient, Map<string, PatientDetail>>();

function getPatientCache(api: ApiClient): Map<string, PatientDetail> {
  let cache = patientCacheByApi.get(api);
  if (!cache) {
    cache = new Map();
    patientCacheByApi.set(api, cache);
  }
  return cache;
}

export function usePatient(id: string | undefined): PatientState {
  const api = useApi();
  const cache = getPatientCache(api);
  const [state, setState] = useState<PatientState>(() =>
    id && cache.has(id) ? { status: "success", patient: cache.get(id)! } : { status: "loading" },
  );

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;

    function load() {
      setState({ status: "loading" });
      api.patients
        .get(id!)
        .then((patient) => {
          if (!cancelled) {
            cache.set(id!, patient);
            setState({ status: "success", patient });
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load patient" });
          }
        });
    }

    const cached = cache.get(id);
    if (cached) {
      setState({ status: "success", patient: cached });
    } else {
      load();
    }

    // A contact edit on the Details tab changes name/email here too — see
    // patientBus.ts. Dropping the cache entry and refetching is simpler than
    // reshaping the PATCH response (PatientDetails) into this hook's
    // PatientDetail, which carries `role` and PatientDetails doesn't.
    const unsubscribe = subscribePatientStale((staleId) => {
      if (staleId === id) {
        cache.delete(id!);
        load();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [api, id, cache]);

  return state;
}

export function patientDisplayName(
  patient: Pick<PatientSummary, "givenName" | "familyName" | "email">,
  unnamedFallback = "Unnamed patient",
): string {
  const fullName = [patient.givenName, patient.familyName].filter(Boolean).join(" ");
  if (fullName) {
    return fullName;
  }
  return patient.email ?? unnamedFallback;
}
